import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  certificacoesApi,
  type CertificadoResumo,
  type NovoCertificado,
  type NovoVinculo,
  type Selo,
} from '@/api/certificacoes'

const CHAVE_LISTA = ['certificacoes'] as const
const CHAVE_SELOS = ['certificacoes-selos'] as const

export function useCertificados() {
  return useQuery<CertificadoResumo[]>({
    queryKey: CHAVE_LISTA,
    queryFn: async () => (await certificacoesApi.list()).data.items,
  })
}

export function useCertificado(id: number | null) {
  return useQuery({
    queryKey: ['certificacao', id],
    queryFn: async () => (await certificacoesApi.detalhe(id as number)).data,
    enabled: id != null,
  })
}

/** Os selos de TODOS os tenants, numa consulta.
 *
 *  A linha da árvore de tenants chama este hook por conta própria em vez de receber o mapa
 *  por prop: são quatro componentes de distância (`TenantsOuTreeView` → `OuBranchNode` →
 *  `SubOuCollapseGroup` → `EnvironmentRow`), e o react-query resolve as N chamadas na MESMA
 *  requisição pela chave. É a forma do `useTenantBudgetStatus`, que já faz isso por linha.
 *
 *  `staleTime` alto porque certificado não muda no minuto: a tela de administração invalida
 *  a chave quando registra ou vincula.
 */
export function useSelosDeCertificacao(options?: { enabled?: boolean }) {
  return useQuery<Record<string, Selo>>({
    queryKey: CHAVE_SELOS,
    queryFn: async () => (await certificacoesApi.selos()).data.selos,
    staleTime: 5 * 60 * 1000,
    // Quem não tem `certificacoes:read` toma 403 aqui, e isso não é erro de tela: a linha
    // simplesmente não mostra selo. Sem `retry: false` o react-query tenta três vezes.
    retry: false,
    enabled: options?.enabled ?? true,
  })
}

/** Invalida as duas chaves: mexer no vínculo muda a lista E o selo de quem estava na tabela. */
function useInvalidar() {
  const qc = useQueryClient()
  return () => {
    qc.invalidateQueries({ queryKey: CHAVE_LISTA })
    qc.invalidateQueries({ queryKey: CHAVE_SELOS })
    // `has_pci` é derivado do vínculo: a tabela de tenants mostra o valor e fica velha.
    qc.invalidateQueries({ queryKey: ['admin-tenants'] })
  }
}

export function useRegistrarCertificado() {
  const invalidar = useInvalidar()
  return useMutation({
    mutationFn: async (corpo: NovoCertificado) =>
      (await certificacoesApi.registrar(corpo)).data,
    onSuccess: invalidar,
  })
}

export function useRevogarCertificado() {
  const invalidar = useInvalidar()
  return useMutation({
    mutationFn: async (id: number) => (await certificacoesApi.revogar(id)).data,
    onSuccess: invalidar,
  })
}

export function useVincularAmbiente(certificadoId: number) {
  const invalidar = useInvalidar()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (corpo: NovoVinculo) =>
      (await certificacoesApi.vincular(certificadoId, corpo)).data,
    onSuccess: () => {
      invalidar()
      qc.invalidateQueries({ queryKey: ['certificacao', certificadoId] })
    },
  })
}

/** Vincular ou desvincular o documento da KB. `null` desvincula. */
export function useVincularDocumento(certificadoId: number) {
  const invalidar = useInvalidar()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (documentoKbId: number | null) =>
      (await certificacoesApi.vincularDocumento(certificadoId, documentoKbId)).data,
    onSuccess: () => {
      invalidar()
      qc.invalidateQueries({ queryKey: ['certificacao', certificadoId] })
    },
  })
}

export function useDesvincularAmbiente(certificadoId: number) {
  const invalidar = useInvalidar()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (tenantId: number) =>
      (await certificacoesApi.desvincular(certificadoId, tenantId)).data,
    onSuccess: () => {
      invalidar()
      qc.invalidateQueries({ queryKey: ['certificacao', certificadoId] })
    },
  })
}
